import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { id: 'hero', label: 'Início' },
    { id: 'services', label: 'Serviços' },
    { id: 'about', label: 'Sobre' },
    { id: 'projects', label: 'Projetos' },
    { id: 'contact', label: 'Contato' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center"
          >
            <img 
              src="/images/logo.png" 
              alt="FaliFe Tecnologia" 
              className="h-16 w-auto hover:scale-105 transition-transform duration-300"
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                // Fallback para logo alternativo se o principal não carregar
                const target = e.target as HTMLImageElement;
                target.src = "/images/img_falife_logo.png";
              }}
            />
          </motion.div>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => scrollToSection(item.id)}
                className={`font-medium hover:text-primary-500 transition-colors duration-200 ${
                  isScrolled ? 'text-secondary-700' : 'text-white'
                }`}
              >
                {item.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => scrollToSection('contact')}
              className={`btn-primary ${isScrolled ? '' : 'bg-white/20 border border-white/30'}`}
            >
              Fale Conosco
            </motion.button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`block h-0.5 w-6 bg-current transform transition duration-300 ${
                isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
              } ${isScrolled ? 'bg-secondary-700' : 'bg-white'}`} />
              <span className={`block h-0.5 w-6 bg-current transform transition duration-300 ${
                isMobileMenuOpen ? 'opacity-0' : 'opacity-100 mt-1'
              } ${isScrolled ? 'bg-secondary-700' : 'bg-white'}`} />
              <span className={`block h-0.5 w-6 bg-current transform transition duration-300 ${
                isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : 'mt-1'
              } ${isScrolled ? 'bg-secondary-700' : 'bg-white'}`} />
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          initial={false}
          animate={{ height: isMobileMenuOpen ? 'auto' : 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden bg-white rounded-lg shadow-lg"
        >
          <div className="py-4 space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-4 py-2 text-secondary-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-2 font-medium text-primary-600 hover:bg-primary-50 transition-colors"
            >
              Fale Conosco
            </button>
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;
